import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface LiveSession {
  id: string
  tenant_id: string
  application_id: string
  user_id: string
  type: string
  reference_id: string | null
  status: 'active' | 'paused' | 'ended'
  title: string
  subtitle: string | null
  progress: number
  priority: 'default' | 'high' | 'low'
  data: string | null
  started_at: string
  expires_at: string | null
  ended_at: string | null
  created_at: string
  updated_at: string
}

interface LiveSessionsParams {
  application_id?: string
  status?: 'active' | 'paused' | 'ended'
  page?: number
  limit?: number
}

export function useLiveSessions(params: LiveSessionsParams = {}) {
  const queryParams = new URLSearchParams()
  if (params.application_id) queryParams.set('application_id', params.application_id)
  if (params.status) queryParams.set('status', params.status)
  if (params.page) queryParams.set('page', String(params.page))
  if (params.limit) queryParams.set('limit', String(params.limit))

  const queryString = queryParams.toString()
  const path = `/dashboard/live-sessions${queryString ? `?${queryString}` : ''}`

  return useQuery({
    queryKey: ['liveSessions', params],
    queryFn: () => api.get<LiveSession[]>(path),
    refetchInterval: params.status === 'active' ? 5000 : false, // Poll active sessions
  })
}

export function useLiveSession(id: string) {
  return useQuery({
    queryKey: ['liveSessions', id],
    queryFn: () => api.get<LiveSession>(`/dashboard/live-sessions/${id}`),
    enabled: !!id,
  })
}

interface CreateLiveSessionData {
  application_id: string
  audience: {
    user_id?: string
  }
  type: string
  reference_id?: string
  title: string
  subtitle?: string
  progress?: number
  priority?: 'default' | 'high' | 'low'
  data?: Record<string, unknown>
  expires_at?: string
}

export function useCreateLiveSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateLiveSessionData) => {
      return api.post<LiveSession>('/v1/live', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liveSessions'] })
    },
  })
}

interface UpdateLiveSessionData {
  title?: string
  subtitle?: string | null
  progress?: number
  status?: 'active' | 'paused' | 'ended'
  data?: Record<string, unknown>
}

export function useUpdateLiveSession(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateLiveSessionData) => {
      return api.patch<LiveSession>(`/v1/live/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liveSessions'] })
      queryClient.invalidateQueries({ queryKey: ['liveSessions', id] })
    },
  })
}

interface EndLiveSessionData {
  id: string
  status?: 'completed' | 'cancelled'
  final_title?: string
  final_subtitle?: string
}

export function useEndLiveSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: EndLiveSessionData) => {
      return api.post<LiveSession>(`/v1/live/${id}/end`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liveSessions'] })
    },
  })
}
